/**
 * Reveal-screen "by president" breakdown (feedback round 1: "call out by
 * president the percentage of policies I agreed/disagreed with, at least
 * the most recent ones if there's enough data" + "show how the user voted
 * vs how the President did"). Pure — no DOM, no fetch — so it's unit
 * testable like state.ts; main.ts wires it to the page.
 */
import { isSuppressed, type AggregatesResponseBody, type Cell, type PartyBucketKey } from "./aggregates.ts";
import type { RatingPayload, RevealItem } from "./types.ts";

/** Reverse-chronological, most-recent-first — Trump is currently serving a
 * second, non-consecutive term, so he leads Biden despite Biden's term being
 * more recent in wall-clock terms. Deliberately a fixed list, not derived
 * from the deck, since the deck has no explicit term-order field — revisit
 * if a future administration is added (see issues.md). */
export const ADMIN_ORDER = [
  "Trump", "Biden", "Obama", "Bush 43", "Clinton", "Bush 41",
  "Reagan", "Carter", "Ford", "Nixon", "Johnson", "Eisenhower",
] as const;

/** A user needs at least this many decided (non-neutral) ratings for a given
 * president before we show their personal percentage — avoids a misleading
 * "100%" off a single card. */
export const MIN_USER_ITEMS = 2;

/** A party bucket needs at least this many decided ratings, summed across
 * every policy tied to a president, before its crowd percentage is shown —
 * same reasoning as MIN_USER_ITEMS, just for the aggregate side. */
export const MIN_CROWD_ITEMS = 5;

export interface PartyPct {
  pct: number | null;
  n: number;
}

export interface PresidentRow {
  administration: string;
  userPct: number | null;
  userN: number;
  votedFor: boolean;
  byParty: Record<PartyBucketKey, PartyPct>;
}

function pctOf(agree: number, disagree: number, minN: number): number | null {
  const decided = agree + disagree;
  return decided < minN ? null : agree / decided;
}

/**
 * `ratings` is the rater's own submission; `reveal` is the matching
 * SubmitResponseBody.reveal (only source of policy_id -> administration,
 * since the blind public deck never carries it). `aggregates` is the latest
 * GET /api/aggregates fetch, or null if it hasn't loaded yet — rows still
 * render with the user's own percentage in that case, just no crowd column.
 * `votedFor` is the set of administration names the rater said they voted
 * for (config.ts VOTE_TO_ADMINISTRATION, from their voted_2020/voted_2024
 * answers).
 */
export function buildPresidentBreakdown(
  ratings: RatingPayload[],
  reveal: RevealItem[],
  aggregates: AggregatesResponseBody | null,
  votedFor: ReadonlySet<string>,
): PresidentRow[] {
  const ratingByPolicy = new Map(ratings.map((r) => [r.p, r.v]));
  const byAdmin = new Map<string, { agree: number; disagree: number; decided: number; policyIds: string[] }>();

  for (const item of reveal) {
    const v = ratingByPolicy.get(item.policy_id);
    if (v === undefined) continue;
    let entry = byAdmin.get(item.administration);
    if (!entry) {
      entry = { agree: 0, disagree: 0, decided: 0, policyIds: [] };
      byAdmin.set(item.administration, entry);
    }
    entry.policyIds.push(item.policy_id);
    if (v > 0) { entry.agree += 1; entry.decided += 1; }
    else if (v < 0) { entry.disagree += 1; entry.decided += 1; }
  }

  const rows: PresidentRow[] = [];
  for (const administration of ADMIN_ORDER) {
    const entry = byAdmin.get(administration);
    if (!entry || entry.decided < MIN_USER_ITEMS) continue;

    const byParty = {} as Record<PartyBucketKey, PartyPct>;
    const partyTotals = new Map<PartyBucketKey, { agree: number; disagree: number }>();
    if (aggregates) {
      for (const policyId of entry.policyIds) {
        const summary = aggregates.by_policy[policyId];
        if (!summary) continue;
        for (const [party, counts] of Object.entries(summary.by_party) as [PartyBucketKey, Cell<{ agree: number; disagree: number }>][]) {
          // A suppressed (too-few-respondents) cell contributes nothing.
          if (isSuppressed(counts)) continue;
          const totals = partyTotals.get(party) ?? { agree: 0, disagree: 0 };
          totals.agree += counts.agree;
          totals.disagree += counts.disagree;
          partyTotals.set(party, totals);
        }
      }
    }
    for (const party of ["D", "R", "I", "other", "unknown"] as PartyBucketKey[]) {
      const totals = partyTotals.get(party) ?? { agree: 0, disagree: 0 };
      byParty[party] = { pct: pctOf(totals.agree, totals.disagree, MIN_CROWD_ITEMS), n: totals.agree + totals.disagree };
    }

    rows.push({
      administration,
      userPct: pctOf(entry.agree, entry.disagree, MIN_USER_ITEMS),
      userN: entry.decided,
      votedFor: votedFor.has(administration),
      byParty,
    });
  }

  return rows;
}
